// @flow
import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape } from 'react-intl';
import EmptyStateImage from './EmptyStateImageComponent';
import * as emptyStateService from '../../services/empty-state/EmptyStateService';
import { getMessageText } from '../../services/message/MessageService';
import { getProductTypeIntlSingularKey, isFilterApplied } from '../../services/product-type/ProductTypeService';
import productEvents from '../../../analytics/events/products';
import AnalyticsClient from '../../../clients/AnalyticsClient';
import type { ContactDetails } from '../../services/empty-state/EmptyStateService';

import './EmptyState.scss';

type EmptyStateData = {
  image: string,
  params?: {
    title?: string | Object, // EmptyStateService expects an object for Intl message construction
    text?: string | Object,
    fullUploadText?: string,
  },
  contactDetails?: ContactDetails,
  title: {
    defaultMessage: string,
    id: string,
  },
  text?: {
    defaultMessage: string,
    id: string,
  },
  fullUploadText?: {
    defaultMessage: string,
    id: string,
  },
};

const emptyBackgroundClass = 'html--empty';

/**
 * Displays the empty state corresponding message
 */
class EmptyStateComponent extends React.Component {
  componentDidMount() {
    AnalyticsClient.trackEvent(productEvents.emptyState());
    this.props.actions.toggleDOMClass({
      selector: 'html',
      className: emptyBackgroundClass,
      type: 'add',
    });
  }

  componentWillUnmount() {
    this.props.actions.toggleDOMClass({
      selector: 'html',
      className: emptyBackgroundClass,
      type: 'remove',
    });
  }

  getEmptyStateData() {
    const {
      productTypeId,
      permissions,
      productSizesAvailable,
      contacts,
      isApprovalsView,
      isAccessoriesView,
    } = this.props;
    const productTypeMessage = getProductTypeIntlSingularKey(productTypeId);
    const productName = getMessageText(this.context.intl, {
      message: productTypeMessage,
      fallbackText: productTypeId,
    });

    const requestData = {
      contacts,
      productName,
      productTypeId,
      permissions,
      productSizesAvailable,
      isApprovalsView,
      isAccessoriesView,
      hasFilters: isFilterApplied(this.props),
    };

    return {
      desktop: emptyStateService.getDesktopEmptyStateMessage(requestData),
      mobile: emptyStateService.getMobileEmptyStateImage(requestData),
    };
  }

  renderContactPersonDetails(contactDetails: ?ContactDetails) {
    if (!contactDetails) {
      return null;
    }
    return (
      <div className="empty-state-component__contact">
        <div className="empty-state-component__contact-name u-fontFamily-secondaryMedium">
          {`${contactDetails.firstName} ${contactDetails.lastName}`}
        </div>
        {contactDetails.phone && <div className="empty-state-component__contact-phone">{contactDetails.phone}</div>}
        <div className="empty-state-component__contact-email u-fontFamily-secondaryMedium">{contactDetails.email}</div>
      </div>
    );
  }

  renderFullUploadText(data: EmptyStateData) {
    if (!data.fullUploadText || Object.keys(data.fullUploadText).length === 0) {
      return null;
    }

    const fullUploadParams = data.params ? data.params.fullUploadText : {};

    return (
      <div className="empty-state-component__full-upload-text">
        <FormattedMessage {...data.fullUploadText} values={fullUploadParams} />
      </div>
    );
  }

  renderEmptyState(data: EmptyStateData, wrapperProps: { mode: string }) {
    const titleParams = data.params ? data.params.title : {};
    const textParams = data.params ? data.params.text : {};

    return (
      <EmptyStateImage image={data.image} {...wrapperProps}>
        <div className="empty-state-component__title u-fontFamily-primaryMedium">
          <FormattedMessage {...data.title} values={titleParams} />
        </div>
        <div className="empty-state-component__text">
          <FormattedMessage {...data.text} values={textParams} />
        </div>
        {this.renderContactPersonDetails(data.contactDetails)}
        {this.renderFullUploadText(data)}
      </EmptyStateImage>
    );
  }

  render() {
    const emptyStateData = this.getEmptyStateData();

    return (
      <div className="empty-state-component u-fontFamily-secondary">
        <div className="empty-state-component__container">
          {this.renderEmptyState(emptyStateData.desktop, { mode: 'desktop' })}
          {this.renderEmptyState(emptyStateData.mobile, { mode: 'mobile' })}
        </div>
      </div>
    );
  }
}

const ContactShape = PropTypes.shape({
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
  phone: PropTypes.string,
});

EmptyStateComponent.displayName = 'EmptyState';

EmptyStateComponent.propTypes = {
  productTypeId: PropTypes.string.isRequired,
  productSizesAvailable: PropTypes.object.isRequired,
  permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
  isApprovalsView: PropTypes.bool,
  contacts: PropTypes.shape({
    toPopulateProductPage: ContactShape,
  }),
  actions: PropTypes.shape({
    toggleDOMClass: PropTypes.func.isRequired,
  }),
};

EmptyStateComponent.contextTypes = {
  intl: intlShape.isRequired,
};

export default EmptyStateComponent;
